import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authLogin, authRegister, authGoogleLogin, authRefreshToken, authGetMe, authLogout, authUpdateProfile } from '../services/api';

const AuthContext = createContext(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef(null);

  const scheduleRefresh = useCallback((accessToken) => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    try {
      const payload = JSON.parse(atob(accessToken.split('.')[1]));
      const expiresIn = (payload.exp * 1000) - Date.now();
      const refreshIn = Math.max(expiresIn - 5 * 60 * 1000, 10000);
      refreshTimerRef.current = setTimeout(async () => {
        try {
          const storedRefresh = localStorage.getItem('ayni-refresh-token');
          if (!storedRefresh) return;
          const data = await authRefreshToken(storedRefresh);
          localStorage.setItem('ayni-access-token', data.access_token);
          localStorage.setItem('ayni-refresh-token', data.refresh_token);
          scheduleRefresh(data.access_token);
        } catch {
          handleLogout();
        }
      }, refreshIn);
    } catch {
      // Can't parse token
    }
  }, []);

  const handleAuthSuccess = useCallback((data) => {
    localStorage.setItem('ayni-access-token', data.access_token);
    localStorage.setItem('ayni-refresh-token', data.refresh_token);
    setUser(data.user);
    scheduleRefresh(data.access_token);
  }, [scheduleRefresh]);

  const handleLogout = useCallback(() => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    localStorage.removeItem('ayni-access-token');
    localStorage.removeItem('ayni-refresh-token');
    setUser(null);
  }, []);

  const login = useCallback(async (email, password) => {
    const data = await authLogin(email, password);
    handleAuthSuccess(data);
    return data.user;
  }, [handleAuthSuccess]);

  const register = useCallback(async (name, email, password) => {
    const data = await authRegister(name, email, password);
    handleAuthSuccess(data);
    return data.user;
  }, [handleAuthSuccess]);

  const googleLogin = useCallback(async (idToken) => {
    const data = await authGoogleLogin(idToken);
    handleAuthSuccess(data);
    return data.user;
  }, [handleAuthSuccess]);

  const updateUser = useCallback(async (data) => {
    const result = await authUpdateProfile(data);
    setUser(result.user);
    return result.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authLogout();
    } catch {
      // ignore — still clear local state
    }
    handleLogout();
  }, [handleLogout]);

  useEffect(() => {
    async function init() {
      const accessToken = localStorage.getItem('ayni-access-token');
      const refreshToken = localStorage.getItem('ayni-refresh-token');
      if (!accessToken) {
        setLoading(false);
        return;
      }
      try {
        const data = await authGetMe();
        setUser(data.user);
        scheduleRefresh(accessToken);
      } catch {
        if (refreshToken) {
          try {
            const refreshData = await authRefreshToken(refreshToken);
            localStorage.setItem('ayni-access-token', refreshData.access_token);
            localStorage.setItem('ayni-refresh-token', refreshData.refresh_token);
            const meData = await authGetMe();
            setUser(meData.user);
            scheduleRefresh(refreshData.access_token);
          } catch {
            handleLogout();
          }
        } else {
          handleLogout();
        }
      }
      setLoading(false);
    }
    init();
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isSuperadmin = user?.role === 'superadmin';
  const tier = user?.tier || 'starter';
  const tierLimits = user?.tier_limits || {};
  const hasFeature = (feature) => isSuperadmin || tierLimits[feature] === true;

  const value = {
    user,
    loading,
    login,
    register,
    googleLogin,
    logout,
    updateUser,
    isAuthenticated: !!user,
    isSuperadmin: user?.role === 'superadmin',
    tier,
    tierLimits,
    hasFeature,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
